import React, { useState, useEffect } from 'react';
import './RequestModal.css';
import { logProductRequest } from './requestsHelpers';

// ── RequestModal ──────────────────────────────────────────────────────────────
//
// Props:
//   isOpen             — controls visibility
//   onClose()          — called to dismiss
//   onSuccess()        — called after successful submit
//   initialProductName — pre-fills the product name field
//   initialStore       — pre-fills store (optional)
//   initialProductId   — stored as requestedFromProductId (optional)
//   showRequestType    — true on product page: shows "What's missing?" radios
//                        and shows only the contextual field for that selection.
//                        false (search): shows product name + all optional fields.

const STORES = ['Tesco', 'Sainsburys', 'Aldi', 'Lidl'];

const REQUEST_TYPES = [
  { value: 'missing_store',   label: 'This product at another store' },
  { value: 'missing_variant', label: 'A different type or size'      },
  { value: 'missing_brand',   label: 'A specific brand'              },
  { value: 'other',           label: 'Something else'                },
];

// ── StoreSelector ─────────────────────────────────────────────────────────────
// Two-step: "Any / not sure" vs "A specific store" → shows dropdown.
// Used in both product-page and search contexts.
function StoreSelector({ store, onStoreChange, label = 'Where is it missing?' }) {
  const [mode, setMode] = useState(store ? 'specific' : 'any');

  const handleModeChange = (next) => {
    setMode(next);
    if (next === 'any') onStoreChange('');
  };

  return (
    <div className="rqm-field">
      <label className="rqm-label">
        {label} <span className="rqm-optional">optional</span>
      </label>
      <div className="rqm-store-mode-group">
        <label className={`rqm-store-mode-opt${mode === 'any' ? ' selected' : ''}`}>
          <input
            type="radio"
            name="rqm-store-mode"
            checked={mode === 'any'}
            onChange={() => handleModeChange('any')}
          />
          <span>Any store / not sure</span>
        </label>
        <label className={`rqm-store-mode-opt${mode === 'specific' ? ' selected' : ''}`}>
          <input
            type="radio"
            name="rqm-store-mode"
            checked={mode === 'specific'}
            onChange={() => handleModeChange('specific')}
          />
          <span>A specific store</span>
        </label>
      </div>
      {mode === 'specific' && (
        <select
          className="rqm-input rqm-select rqm-store-dropdown"
          value={store}
          onChange={e => onStoreChange(e.target.value)}
          autoFocus
        >
          <option value="">Select store</option>
          {STORES.map(s => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function RequestModal({
  isOpen,
  onClose,
  onSuccess,
  initialProductName = '',
  initialStore       = '',
  initialProductId   = '',
  showRequestType    = false,
}) {
  const [productName, setProductName] = useState('');
  const [requestType, setRequestType] = useState('missing_store');
  const [store,       setStore]       = useState('');
  const [brand,       setBrand]       = useState('');
  const [variant,     setVariant]     = useState('');
  const [comment,     setComment]     = useState('');
  const [submitted,   setSubmitted]   = useState(false);

  // Sync initial values whenever the modal opens
  useEffect(() => {
    if (isOpen) {
      setProductName(initialProductName);
      setStore(initialStore);
      setRequestType('missing_store');
      setBrand('');
      setVariant('');
      setComment('');
      setSubmitted(false);
    }
  }, [isOpen, initialProductName, initialStore]);

  // Submit is blocked when "Something else" is selected but the comment is empty
  const commentRequired = showRequestType && requestType === 'other';
  const canSubmit = productName.trim() && !(commentRequired && !comment.trim());

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    const resolvedType = showRequestType
      ? requestType
      : (store ? 'missing_store' : 'new_family');

    logProductRequest({
      productName:            productName.trim(),
      requestType:            resolvedType,
      ...(store.trim()      ? { store:   store.trim()   } : {}),
      ...(brand.trim()      ? { brand:   brand.trim()   } : {}),
      ...(variant.trim()    ? { variant: variant.trim() } : {}),
      ...(comment.trim()    ? { comment: comment.trim() } : {}),
      ...(initialProductId  ? { requestedFromProductId: initialProductId } : {}),
      source: showRequestType ? 'product_page' : 'search',
    });

    setSubmitted(true);
    onSuccess?.();
    setTimeout(() => onClose(), 1600);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="rqm-backdrop" onClick={onClose} />

      <div className="rqm-sheet" role="dialog" aria-modal="true" aria-label="Request a product">
        <div className="sheet-handle" />

        {submitted ? (
          /* ── Success state ── */
          <div className="rqm-success">
            <span className="rqm-success-icon" aria-hidden="true">✓</span>
            <p className="rqm-success-title">Request saved</p>
            <p className="rqm-success-sub">We'll look into adding this.</p>
          </div>
        ) : (
          /* ── Form ── */
          <>
            <div className="rqm-header">
              <h3 className="rqm-title">
                {showRequestType ? 'Missing something?' : 'Request a product'}
              </h3>
              <button className="rqm-close" onClick={onClose} aria-label="Close">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="4" y1="4" x2="12" y2="12"/>
                  <line x1="12" y1="4" x2="4" y2="12"/>
                </svg>
              </button>
            </div>

            <form className="rqm-form" onSubmit={handleSubmit}>

              {/* ── Product identity ── */}
              {showRequestType ? (
                <div className="rqm-product-context">
                  <span className="rqm-product-context-label">Product</span>
                  <span className="rqm-product-context-name">{productName}</span>
                </div>
              ) : (
                <div className="rqm-field">
                  <label className="rqm-label" htmlFor="rqm-product">Product name</label>
                  <input
                    id="rqm-product"
                    className="rqm-input"
                    type="text"
                    value={productName}
                    onChange={e => setProductName(e.target.value)}
                    placeholder="e.g. Cottage cheese"
                    required
                    autoFocus
                  />
                </div>
              )}

              {/* ── What's missing? (product page only) ── */}
              {showRequestType && (
                <div className="rqm-field">
                  <label className="rqm-label">What's missing?</label>
                  <div className="rqm-type-group">
                    {REQUEST_TYPES.map(opt => (
                      <label
                        key={opt.value}
                        className={`rqm-type-option${requestType === opt.value ? ' selected' : ''}`}
                      >
                        <input
                          type="radio"
                          name="rqm-request-type"
                          value={opt.value}
                          checked={requestType === opt.value}
                          onChange={() => setRequestType(opt.value)}
                        />
                        <span className="rqm-type-label">{opt.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Contextual field — product page ── */}
              {showRequestType && requestType === 'missing_store' && (
                <StoreSelector store={store} onStoreChange={setStore} />
              )}

              {showRequestType && requestType === 'missing_variant' && (
                <div className="rqm-field">
                  <label className="rqm-label" htmlFor="rqm-variant">
                    Type or size <span className="rqm-optional">optional</span>
                  </label>
                  <input
                    id="rqm-variant"
                    className="rqm-input"
                    type="text"
                    value={variant}
                    onChange={e => setVariant(e.target.value)}
                    placeholder="e.g. mature, 500g, full fat"
                    autoFocus
                  />
                </div>
              )}

              {showRequestType && requestType === 'missing_brand' && (
                <div className="rqm-field">
                  <label className="rqm-label" htmlFor="rqm-brand">
                    Brand <span className="rqm-optional">optional</span>
                  </label>
                  <input
                    id="rqm-brand"
                    className="rqm-input"
                    type="text"
                    value={brand}
                    onChange={e => setBrand(e.target.value)}
                    placeholder="e.g. Heinz, Lurpak"
                    autoFocus
                  />
                </div>
              )}

              {showRequestType && requestType === 'other' && (
                <div className="rqm-field">
                  <label className="rqm-label" htmlFor="rqm-comment">
                    Tell us what's missing
                    {/* required — no optional tag */}
                  </label>
                  <textarea
                    id="rqm-comment"
                    className="rqm-input rqm-textarea"
                    value={comment}
                    onChange={e => setComment(e.target.value)}
                    placeholder="e.g. low fat version, 1kg pack, Lidl version"
                    rows={3}
                    required
                    autoFocus
                  />
                </div>
              )}

              {/* ── All optional fields — search context only ── */}
              {!showRequestType && (
                <>
                  <StoreSelector
                    store={store}
                    onStoreChange={setStore}
                    label="Where is it missing?"
                  />

                  <div className="rqm-field">
                    <label className="rqm-label" htmlFor="rqm-brand">
                      Brand <span className="rqm-optional">optional</span>
                    </label>
                    <input
                      id="rqm-brand"
                      className="rqm-input"
                      type="text"
                      value={brand}
                      onChange={e => setBrand(e.target.value)}
                      placeholder="e.g. Heinz, own brand"
                    />
                  </div>

                  <div className="rqm-field">
                    <label className="rqm-label" htmlFor="rqm-variant">
                      Type or size <span className="rqm-optional">optional</span>
                    </label>
                    <input
                      id="rqm-variant"
                      className="rqm-input"
                      type="text"
                      value={variant}
                      onChange={e => setVariant(e.target.value)}
                      placeholder="e.g. mature, 500g, full fat"
                    />
                  </div>
                </>
              )}

              <button
                className="rqm-submit"
                type="submit"
                disabled={!canSubmit}
              >
                Submit request
              </button>
            </form>
          </>
        )}
      </div>
    </>
  );
}
